import { sendPlaylog, sendTrackingLog } from '../data/api'
import { loadNextAd } from '../player/player'
import { setupMediaErrorTracking, vastErrorTracking } from '../utils/vast-error-handler'
import { clearVastTracking, handleVastTracking, triggerTrackingEvent } from '../utils/vast-tracking'
import { clearVideoTracking, handleVideoTracking } from '../utils/video-tracking'

let globalLogger = null
const urlParams = new URLSearchParams(window.location.search)
const screenUuid = urlParams.get('screenUuid')

// Function to set global logger from index.js
export const setGlobalLoggerInEvent = (logger) => {
  globalLogger = logger
}

export const handleVideoEvent = (videoElement, adData) => {
  // Remove previous listeners if present
  if (videoElement._videoHandlers) {
    videoElement.removeEventListener('play', videoElement._videoHandlers.onPlay)
    videoElement.removeEventListener('ended', videoElement._videoHandlers.onEnded)
    videoElement.removeEventListener('error', videoElement._videoHandlers.onError)
  }

  // Create new handler functions
  const onPlay = async () => {
    console.log('Video: Started')
    handleVideoTracking(adData)
    await sendPlaylog(adData)

    if (globalLogger) {
      await globalLogger.addEvent('video_playback_started', {
        content_type: 'video',
        video_duration: videoElement.duration,
        url: adData?.url ?? videoElement.src,
        adexchange_id: adData?.source?.adexchange_id,
        autoplay: !videoElement.paused,
        screen_uuid: screenUuid,
        type: adData?.source?.type,
        timestamp: new Date().toISOString(),
        id: adData?.id
      })
    }
  }
  const onEnded = async () => {
    console.log('Video: Complete')
    if (globalLogger) {
      await globalLogger.addEvent('video_playback_completed', {
        content_type: 'video',
        video_duration: videoElement.duration,
        url: adData?.url ?? videoElement.src,
        video_current_time: videoElement.currentTime,
        completion_percentage: videoElement.duration
          ? Math.round((videoElement.currentTime / videoElement.duration) * 100)
          : 0,
        adexchange_id: adData?.source?.adexchange_id,
        screen_uuid: screenUuid,
        type: adData?.source?.type,
        timestamp: new Date().toISOString(),
        id: adData?.id
      })
    }
    clearVideoTracking()
    loadNextAd()
  }
  const onError = (e) => {
    console.error('Video: Error', e)
    loadNextAd()
  }

  videoElement.addEventListener('play', onPlay)
  videoElement.addEventListener('ended', onEnded)
  videoElement.addEventListener('error', onError)
  videoElement._videoHandlers = { onPlay, onEnded, onError }
}

export const handleImageEvent = (imageElement, adData, duration = 15000) => {
  // Remove previous listeners if present
  if (imageElement._imageHandlers) {
    imageElement.removeEventListener('load', imageElement._imageHandlers.onLoad)
    imageElement.removeEventListener('error', imageElement._imageHandlers.onError)
  }
  if (imageElement._imageTimeoutId) {
    clearTimeout(imageElement._imageTimeoutId)
    imageElement._imageTimeoutId = null
  }

  // Create new handler function
  const onLoad = async () => {
    console.log('Image: Loaded')
    await sendPlaylog(adData)

    if (globalLogger) {
      await globalLogger.addEvent('image_content_loaded', {
        content_type: 'image',
        url: adData?.url ?? imageElement.src,
        image_width: imageElement.naturalWidth,
        image_height: imageElement.naturalHeight,
        adexchange_id: adData?.source?.adexchange_id,
        screen_uuid: screenUuid,
        type: adData?.source?.type,
        timestamp: new Date().toISOString()
      })
    }

    imageElement._imageTimeoutId = setTimeout(() => {
      loadNextAd()
      clearTimeout(imageElement._imageTimeoutId)
      imageElement._imageTimeoutId = null
    }, duration)
  }
  const onError = (e) => {
    console.error('Image: Error', e)
    loadNextAd()
  }

  imageElement.addEventListener('load', onLoad)
  imageElement.addEventListener('error', onError)
  imageElement._imageHandlers = { onLoad, onError }
}

export const handleVastEvent = (vastElement, adData) => {
  const adExchangeId = adData?.source?.adexchange_id || null

  // Remove previous listeners if present
  if (vastElement._vastHandlers) {
    vastElement.removeEventListener('play', vastElement._vastHandlers.onPlay)
    vastElement.removeEventListener('ended', vastElement._vastHandlers.onEnded)
    vastElement.removeEventListener('error', vastElement._vastHandlers.onError)
  }

  // Create new handler functions
  const onPlay = async () => {
    handleVastTracking(adExchangeId, adData)
    await sendPlaylog(adData)

    // Comprehensive logging integration
    if (globalLogger) {
      await globalLogger.addEvent('vast_playback_started', {
        content_type: 'vast',
        vast_duration: vastElement?.duration,
        vast_url: vastElement?.src,
        adexchange_id: adData?.source?.adexchange_id,
        url: adData?.url,
        type: adData?.source?.type,
        screen_uuid: screenUuid,
        timestamp: new Date().toISOString(),
        id: adData?.id
      })
    }
  }
  const onEnded = async () => {
    console.log('Vast: Complete')
    triggerTrackingEvent('complete')

    // Comprehensive logging integration
    if (globalLogger) {
      await globalLogger.addEvent('vast_playback_completed', {
        content_type: 'vast',
        vast_duration: vastElement?.duration,
        vast_current_time: vastElement?.currentTime,
        completion_percentage: vastElement?.duration
          ? Math.round((vastElement.currentTime / vastElement.duration) * 100)
          : 0,
        adexchange_id: adExchangeId,
        url: adData?.url,
        type: adData?.source?.type,
        screen_uuid: screenUuid,
        timestamp: new Date().toISOString(),
        id: adData?.id
      })
    }

    if (adExchangeId) {
      await sendTrackingLog({
        eventType: 'complete',
        adexchangeId: adExchangeId
      })
    }

    clearVastTracking()
    loadNextAd()
  }

  const onError = (e) => {
    console.error('Vast: Error', e)
    loadNextAd()
    if (adExchangeId) {
      setupMediaErrorTracking(vastElement, adExchangeId, adData.url)
    }
  }

  vastElement.addEventListener('play', onPlay)
  vastElement.addEventListener('ended', onEnded)
  vastElement.addEventListener('error', onError)
  vastElement._vastHandlers = { onPlay, onEnded, onError }
}
